import * as React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { AppRoutes } from 'constants/routes';

import { getAssetString, getEnvClassicAssetData } from 'helpers/assets';
import { createLumen } from 'helpers/token';

import { ModalService } from 'services/globalServices';

import ArrowRight from 'assets/icons/arrows/arrow-right-16.svg';
import CardIcon from 'assets/icons/objects/icon-card-24.svg';
import SwapIcon from 'assets/icons/objects/icon-swap-24.svg';

import { ExternalLink } from 'basics/links';
import { ModalDescription, ModalTitle, ModalWrapper } from 'basics/ModalAtoms';

import { respondDown } from 'styles/mixins';
import { Breakpoints, COLORS } from 'styles/style-constants';

const Container = styled.div`
    width: 100%;
`;

const Options = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.6rem;
    margin-top: 0.8rem;
`;

const Chevron = styled.div`
    display: flex;
    align-items: center;
    flex-shrink: 0;
    color: ${COLORS.textPrimary};
    transition: transform 0.2s;
`;

const Option = styled(Link)`
    display: flex;
    align-items: center;
    gap: 2rem;
    padding: 2.4rem;
    border-radius: 1.6rem;
    background: ${COLORS.gray50};
    text-decoration: none;
    cursor: pointer;

    &:hover ${Chevron} {
        transform: translateX(0.5rem);
    }

    ${respondDown(Breakpoints.md)`
        padding: 1.6rem;
        gap: 1.2rem;
    `}
`;

const IconBox = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: ${COLORS.textPrimary};

    svg {
        width: 2.4rem;
        height: 2.4rem;
    }
`;

const OptionBody = styled.div`
    flex: 1;
`;

const OptionTitle = styled.div`
    font-size: 1.8rem;
    line-height: 2.2rem;
    font-weight: 700;
    color: ${COLORS.textPrimary};
    margin-bottom: 0.4rem;
`;

const OptionDescription = styled.div`
    font-size: 1.4rem;
    line-height: 2rem;
    color: ${COLORS.textGray};
`;

const LearnMore = styled(ExternalLink)`
    margin-top: 2.4rem;
`;

const GetAquaModal = (): React.ReactNode => {
    const swapLink = AppRoutes.section.swap.to.index({
        source: getAssetString(createLumen()),
        destination: getEnvClassicAssetData('aqua').assetString,
    });

    const closeModal = () => ModalService.closeAllModals();

    return (
        <ModalWrapper>
            <Container>
                <ModalTitle>Get AQUA</ModalTitle>
                <ModalDescription $smallMarginBottom>
                    Buy AQUA with your preferred payment method or swap existing assets.
                </ModalDescription>

                <Options>
                    <Option to={AppRoutes.page.buyAqua} onClick={closeModal}>
                        <IconBox>
                            <CardIcon />
                        </IconBox>
                        <OptionBody>
                            <OptionTitle>Buy AQUA</OptionTitle>
                            <OptionDescription>
                                Purchase AQUA with your preferred payment method.
                            </OptionDescription>
                        </OptionBody>
                        <Chevron>
                            <ArrowRight />
                        </Chevron>
                    </Option>

                    <Option to={swapLink} onClick={closeModal}>
                        <IconBox>
                            <SwapIcon />
                        </IconBox>
                        <OptionBody>
                            <OptionTitle>Swap AQUA</OptionTitle>
                            <OptionDescription>
                                Convert your existing Stellar assets to AQUA.
                            </OptionDescription>
                        </OptionBody>
                        <Chevron>
                            <ArrowRight />
                        </Chevron>
                    </Option>
                </Options>

                <LearnMore to={AppRoutes.page.token} onClick={closeModal}>
                    Learn more about AQUA token
                </LearnMore>
            </Container>
        </ModalWrapper>
    );
};

export default GetAquaModal;
